import React from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, Clock, Edit, Trash2, Check } from 'lucide-react';
import type { Reminder, ReminderUrgency } from '@/services/reminders.service';
import { dfLocale } from '@/lib/dateFnsLocale';
import { useTranslation } from 'react-i18next';

interface ReminderListProps {
  reminders: Reminder[];
  onEdit: (reminder: Reminder) => void;
  onDelete: (reminderId: string) => void;
  onToggleComplete: (reminderId: string, isCompleted: boolean) => void;
  isLoading?: boolean;
}

const URGENCY_LABELS: Record<ReminderUrgency, string> = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  urgent: 'Dringend',
};

export default function ReminderList({
  reminders,
  onEdit,
  onDelete,
  onToggleComplete,
  isLoading = false,
}: ReminderListProps) {
  const { t } = useTranslation();
  const [deleteReminderId, setDeleteReminderId] = React.useState<string | null>(null);

  const handleDeleteClick = (reminderId: string) => {
    setDeleteReminderId(reminderId);
  };

  const handleDeleteConfirm = () => {
    if (deleteReminderId) {
      onDelete(deleteReminderId);
      setDeleteReminderId(null);
    }
  };

  if (reminders.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground text-sm">
            {t('common:x4.noRemindersForDate', 'Keine Erinnerungen für dieses Datum')}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Sort reminders: incomplete first, then by date
  const sortedReminders = [...reminders].sort((a, b) => {
    if (a.is_completed !== b.is_completed) {
      return a.is_completed ? 1 : -1;
    }
    return new Date(a.reminder_date).getTime() - new Date(b.reminder_date).getTime();
  });

  return (
    <>
      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-3">
          {sortedReminders.map((reminder) => (
            <Card
              key={reminder.id}
              className={`transition-all ${
                reminder.is_completed ? 'opacity-60' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <Checkbox
                    checked={reminder.is_completed}
                    onCheckedChange={(checked) =>
                      onToggleComplete(reminder.id, checked as boolean)
                    }
                    disabled={isLoading}
                    className="mt-1"
                  />

                  {/* Color Indicator */}
                  <div
                    className="w-1 h-full rounded-full mt-1"
                    style={{ backgroundColor: reminder.color }}
                  />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4
                          className={`font-medium text-sm ${
                            reminder.is_completed
                              ? 'line-through text-muted-foreground'
                              : ''
                          }`}
                        >
                          {reminder.title}
                        </h4>
                        {reminder.description && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {reminder.description}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(reminder)}
                          disabled={isLoading}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(reminder.id)}
                          disabled={isLoading}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {URGENCY_LABELS[reminder.urgency]}
                      </Badge>

                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {format(new Date(reminder.reminder_date), 'PPp', {
                          locale: dfLocale(),
                        })}
                      </div>

                      {reminder.notify_before_minutes > 0 && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Bell className="h-3 w-3" />
                          {reminder.notify_before_minutes} min vorher
                        </div>
                      )}

                      {reminder.is_completed && reminder.completed_at && (
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <Check className="h-3 w-3" />
                          {t('common:x4.doneOn', 'Erledigt am')}{' '}
                          {format(new Date(reminder.completed_at), 'PP', {
                            locale: dfLocale(),
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteReminderId !== null}
        onOpenChange={(open) => !open && setDeleteReminderId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common:x4.deleteReminderQ', 'Erinnerung löschen?')}</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Die Erinnerung wird
              dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              {t('common:action.delete', 'Löschen')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
